/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import type { InjectedRouter } from 'react-router';

import { FloatingTooltip } from '@coursera-web/cds-labs';
import { ShareButtonWithModal } from '@coursera-web/sharing-common';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { compose, withProps } from 'recompose';

import Imgix from 'js/components/Imgix';
import { processImage } from 'js/lib/imgix';

import { AlwaysLightMode, Grid, Typography2, breakpoints, typography2 } from '@coursera/cds-core';
import { InformativeIcon, WarningIcon } from '@coursera/cds-icons';

// Save/Unsave Experiment
import SavingBookmark from 'bundles/browse-common/components/bookmark/SavingBookmark';
import { SAVING_BOOKMARK_WHITE } from 'bundles/browse-common/components/bookmark/utils/BookmarkUtils';
import type { Product } from 'bundles/browse-common/types/SavedProductQueries';
import Markdown from 'bundles/cms/components/Markdown';
import { buildDegreeCategoryUrlRelative } from 'bundles/common/utils/urlUtils';
import { EoiWidgetContainer } from 'bundles/expression-of-interest/components/xddp/EoiWidgetContainer';
import { ACTION_TYPES } from 'bundles/expression-of-interest/constants';
import type { ActionTypes } from 'bundles/expression-of-interest/types';
import MobileBreadCrumb from 'bundles/page/components/MobileBreadCrumb';
import { isFakeDegree } from 'bundles/premium-hub/utils/fakeDegreeUtils';
import { savedProductTypes } from 'bundles/unified-common/utils/restQueries';
import Container from 'bundles/xddp/components/shared/Container';
import Navigation from 'bundles/xddp/components/shared/Navigation';
import XDDPBreadcrumbs from 'bundles/xddp/components/shared/XDDPBreadcrumbs';
import withIsProductSavedData from 'bundles/xddp/components/shared/withIsProductSavedData';
import { DEGREES_ON_HOSTED_APP } from 'bundles/xddp/constants';
import type { SharedPremiumHeaderProps, UserAgentProps } from 'bundles/xddp/types';
import type { IsProductSavedInjectedProps as PropsFromIsProductSaved } from 'bundles/xddp/types/withIsProductSavedData';
import getSocialCaptions from 'bundles/xddp/utils/getSocialCaptions';

import _t from 'i18n!nls/xddp';

import 'css!bundles/xddp/components/__styles__/shared/SharedPremiumHeader';

type PropsFromCaller = SharedPremiumHeaderProps & UserAgentProps;
type Props = PropsFromCaller & { product: Product } & PropsFromIsProductSaved;

const styles = {
  breadcrumbsWrapper: css`
    height: 24px; /* Enforces one-line height */
    margin-bottom: 10px;
    overflow: hidden;
    position: relative;

    .breadcrumbs {
      padding: 0;
      font-size: 14px;
      font-weight: 700;

      li {
        display: inline;

        a {
          color: var(--cds-color-neutral-primary-invert);
        }

        .separator {
          margin: 0 8px;
        }
      }
    }

    ${breakpoints.up('sm')} {
      margin-bottom: 48px;
    }
  `,
  bannerNotification: css`
    color: var(--cds-color-neutral-primary-invert);
    display: flex;
    background-color: var(--cds-color-blue-950);
    padding: 12px 0;
    justify-content: center;

    .rc-Markdown {
      p {
        ${typography2.bodyPrimary}
        font-weight: 600;
      }
    }

    .banner-notification-icon {
      margin: 0 8px -6px 12px;
    }

    .banner-notification-text {
      padding-right: 12px;

      p {
        margin: 0;
      }

      a {
        color: var(--cds-color-neutral-primary-invert);
      }
    }
  `,
  shareButtonAlign: css`
    display: inline-block;
    height: 36px;
    vertical-align: bottom;
    ${breakpoints.down('md')} {
      margin-bottom: 1rem;
      display: flex;
    }
  `,
  paddingBottom50: css`
    padding-bottom: 0;
    ${breakpoints.down('md')} {
      padding-bottom: 30px;
    }
  `,
  ratingsText: css`
    font-size: 14px;
    margin-top: 40px;
  `,
  eoiGridItem: css`
    display: flex;
    min-height: 311px;
  `,
  gradientBackgroundWidth: css`
    ${breakpoints.up('xs')} {
      width: 100%;
    }
    ${breakpoints.up('md')} {
      width: 75%;
    }
    ${breakpoints.up('lg')} {
      width: 70%;
    }
    ${breakpoints.up('xl')} {
      width: 65%;
    }
    ${breakpoints.down('sm')} {
      width: 100%;
      transform: none !important;
    }
  `,
  bannerBackgroundImageWidth: css`
    ${breakpoints.up('md')} {
      width: 55%;
    }
    ${breakpoints.up('lg')} {
      width: 60%;
    }
    ${breakpoints.down('sm')} {
      display: none;
    }
  `,
  emptyBreadcrumbsSpacer: css`
    height: 38px;
  `,
  mobileBreadcrumb: css`
    ${breakpoints.up('md')} {
      display: none;
    }
  `,
  desktopBreadcrumb: css`
    ${breakpoints.down('sm')} {
      display: none;
    }
  `,
};

type State = {
  isEmbeddedRegFormDisplayed: boolean;
  eoiInitialStep?: ActionTypes;
};

type RouterWithLocation = InjectedRouter & { location: { hash?: string } }; // Arg having a hard time typing the router. InjectedRouter.location supposedly does not exist?!

type SharedPremiumHeaderContext = {
  _eventData: {};
  router: RouterWithLocation;
};

const routerLocationHasRequestInfoHash = (router: RouterWithLocation) => {
  const { location } = router;
  return location?.hash === '#requestinfo';
};

export class SharedPremiumHeader extends React.Component<Props, State> {
  static contextTypes = {
    _eventData: PropTypes.object,
    router: PropTypes.object.isRequired,
  };

  declare context: SharedPremiumHeaderContext;

  constructor(props: Props, context: SharedPremiumHeaderContext) {
    super(props);
    const { router } = context;
    const eoiInitialStep = routerLocationHasRequestInfoHash(router) ? ACTION_TYPES.emailMeInfo : ACTION_TYPES.initial;
    this.state = {
      isEmbeddedRegFormDisplayed: false,
      eoiInitialStep,
    };
  }

  getBackgroundGradient = () => {
    const { partnerInformation } = this.props;

    if (!partnerInformation?.primaryColor || !partnerInformation?.secondaryColor) {
      return undefined;
    }

    return `linear-gradient(324.97deg, ${partnerInformation?.primaryColor} 0%, ${partnerInformation?.secondaryColor} 100%)`;
  };

  handleShowEmbeddedRegForm = (isEmbeddedRegFormDisplayed: boolean) => {
    this.setState(() => ({ isEmbeddedRegFormDisplayed }));
  };

  hostedAppEnabled = () => {
    return DEGREES_ON_HOSTED_APP.includes(this.props.slug);
  };

  handleHostedAppApplyClick = () => {
    const { onApplyNowClick } = this.props;

    if (this.hostedAppEnabled()) {
      onApplyNowClick();
      this.goToApplicationForm();
    }
  };

  goToApplicationForm = () => {
    const { expressionOfInterestContainer } = this.props;
    if (!expressionOfInterestContainer?.hostedApplicationUrl || !this.hostedAppEnabled()) {
      return;
    }

    window.open(expressionOfInterestContainer.hostedApplicationUrl, '_blank');
  };

  deriveActionBarData = () => {
    const { isStickyEoiPresent, partnerInformation, programName } = this.props;

    return {
      isFixed: isStickyEoiPresent,
      partnerName: partnerInformation?.partnerName || '',
      programName,
      partnerMonotoneLogo: partnerInformation?.partnerMonotoneLogo?.url || undefined,
    };
  };

  deriveHostedAppApplyButtonData = () => {
    const { expressionOfInterestContainer } = this.props;

    return {
      actionType: ACTION_TYPES.apply,
      customPageReloadFunc: this.goToApplicationForm,
      doAction: this.handleHostedAppApplyClick,
      buttonOptions: {
        label: expressionOfInterestContainer?.applyButtonText || _t('Apply'),
      },
    };
  };

  deriveDefaultApplyButtonData = (applicationUrl: $TSFixMe) => {
    if (!applicationUrl) {
      return undefined;
    }
    const { expressionOfInterestContainer } = this.props;

    const { onApplyNowClick } = this.props;

    return {
      actionType: ACTION_TYPES.apply,
      isExternalLink: true,
      doAction: onApplyNowClick,
      buttonOptions: {
        tag: 'a' as const,
        htmlAttributes: {
          href: applicationUrl,
          target: '_blank',
          rel: 'noopener noreferrer',
        },
        label: expressionOfInterestContainer?.applyButtonText || _t('Apply'),
      },
    };
  };

  deriveApplyButtonData = () => {
    const { expressionOfInterestContainer } = this.props;

    if (this.hostedAppEnabled() && expressionOfInterestContainer?.hostedApplicationUrl) {
      return this.deriveHostedAppApplyButtonData();
    }

    if (expressionOfInterestContainer?.applyButtonUrl) {
      return this.deriveDefaultApplyButtonData(expressionOfInterestContainer?.applyButtonUrl);
    }

    return null;
  };

  requestInfoData = () => {
    const { onRequestInfoClick, expressionOfInterestContainer } = this.props;

    return {
      actionType: ACTION_TYPES.emailMeInfo,
      doAction: () => onRequestInfoClick(),
      buttonOptions: {
        label: expressionOfInterestContainer?.emailMeButtonText || _t('Email me info'),
      },
    };
  };

  renderEoiForm = () => {
    const { id, expressionOfInterestContainer, slug } = this.props;
    return (
      <Grid item lg={4} md={4} sm={12} xs={12}>
        <EoiWidgetContainer
          slug={slug}
          degreeId={id}
          applicationUrl={
            expressionOfInterestContainer?.applyButtonUrl || expressionOfInterestContainer?.hostedApplicationUrl || ''
          }
          programInterestText={expressionOfInterestContainer?.programInterestText || ''}
          actionBarData={this.deriveActionBarData()}
          enrollButtonData={this.deriveApplyButtonData()}
          emailMeInfoButtonData={this.requestInfoData()}
          isDegree
        />
      </Grid>
    );
  };

  renderBreadcrumbs = () => {
    const { slug, programName, programCategory, programCategoryId, currentNavElement } = this.props;

    if (programCategory === undefined || programCategoryId === undefined)
      return <div css={styles.emptyBreadcrumbsSpacer} />;

    return (
      <div className="breadcrumbs-wrapper" css={styles.breadcrumbsWrapper}>
        <div css={styles.mobileBreadcrumb}>
          <MobileBreadCrumb
            href={buildDegreeCategoryUrlRelative(programCategoryId)}
            label={`${programCategory} Degrees`}
          />
        </div>
        <div css={styles.desktopBreadcrumb}>
          <XDDPBreadcrumbs
            slug={slug}
            programName={programName}
            programCategory={programCategory}
            programCategoryId={programCategoryId}
            currentNavElement={currentNavElement}
          />
        </div>
      </div>
    );
  };

  renderHeaderContent = () => {
    const { slug, programName, partnerInformation, product, loadingSavedProduct, isSavedProduct } = this.props;

    const partnerName = partnerInformation?.partnerName;
    const partnerLogo = partnerInformation?.partnerLogo;

    return (
      <Container className="content-container">
        <Grid container>
          <Grid item lg={8} md={8} className="program-info-content">
            <AlwaysLightMode>
              {this.renderBreadcrumbs()}
              {partnerName && partnerLogo && partnerLogo.url && (
                <div className="partner-logo-container">
                  <Imgix className="partner-logo" alt={partnerName} src={partnerLogo.url} />
                </div>
              )}
              <Typography2 component="h1" color="invertBody" className="page-title">
                {programName}
              </Typography2>
              <Typography2 component="p" color="invertBody" className="partner-name">
                {partnerName}
                {isFakeDegree(slug) && (
                  <FloatingTooltip
                    message={_t('This degree is still in development and is not currently enrolling students.')}
                  >
                    {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
                    <span tabIndex={0}>
                      <InformativeIcon
                        size="small"
                        color="invert"
                        css={css`
                          margin: 0 8px;
                          cursor: help;
                        `}
                      />
                    </span>
                  </FloatingTooltip>
                )}
              </Typography2>
              <div>
                <div className="share-button-wrap" css={styles.shareButtonAlign}>
                  <ShareButtonWithModal
                    rootClassName="xddp-banner-button"
                    utmMediumParam="page_share"
                    utmContentParam="xddp"
                    utmCampaignParam="banner_button"
                    captions={getSocialCaptions({ programName, partnerName: partnerName || '' })}
                  />
                </div>
              </div>
              {!loadingSavedProduct && (
                <SavingBookmark
                  saved={isSavedProduct}
                  product={product}
                  color={SAVING_BOOKMARK_WHITE}
                  shouldShowText={true}
                />
              )}
            </AlwaysLightMode>
          </Grid>
          {this.renderEoiForm()}
        </Grid>
      </Container>
    );
  };

  renderHeaderContainer = () => {
    const { partnerInformation } = this.props;

    return (
      <div className="header-container">
        <div
          className="gradient-background"
          style={{ background: this.getBackgroundGradient() }}
          css={styles.gradientBackgroundWidth}
        />
        {partnerInformation?.bannerImage && (
          <div
            className="background-image"
            style={{ backgroundImage: `url('${processImage(partnerInformation?.bannerImage.url)}')` }}
            css={styles.bannerBackgroundImageWidth}
          />
        )}
        {this.renderHeaderContent()}
      </div>
    );
  };

  render() {
    const {
      slug,
      navigationElements,
      bannerNotification,
      currentNavElement,
      onApplyNowClick,
      isStickyBarPresent,
      userAgent,
    } = this.props;

    const { isEmbeddedRegFormDisplayed } = this.state;

    const classNames = classnames('rc-XDDPHeader', {
      'thin-nav-sticky': isStickyBarPresent,
    });

    const isEoiHeaderPresent = !isEmbeddedRegFormDisplayed;

    return (
      <div className={classNames}>
        {bannerNotification && (
          <div className="banner-notification" css={styles.bannerNotification}>
            <span aria-hidden="true">
              <WarningIcon size="large" className="banner-notification-icon" />
            </span>
            <Markdown className="banner-notification-text" source={bannerNotification} onLinkClick={onApplyNowClick} />
          </div>
        )}
        {this.renderHeaderContainer()}
        <Navigation
          userAgent={userAgent}
          slug={slug}
          isFixed={isStickyBarPresent}
          navigationElements={navigationElements}
          currentNavElement={currentNavElement}
          isHeaderPresent={isEoiHeaderPresent}
        />
      </div>
    );
  }
}

// NOTE: work remaining - convert to compose/withProps setup, build out product prop, and then pass saved when appropriate
export default compose<Props, PropsFromCaller>(
  withProps<{ product: Product }, PropsFromCaller>(({ id }) => {
    return {
      product: {
        product_id: id, // eslint-disable-line camelcase
        product_type: savedProductTypes.DEGREE,
      },
    };
  }),
  withIsProductSavedData
)(SharedPremiumHeader);

export const BaseComp = SharedPremiumHeader;
